import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../../services/config.service';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { TaskService } from '../../services/task.service';
import { WorkflowUtilitiesService } from '../../services/workflow-utilities.service';
import { EditorQueriesComponent } from '../editor-queries/editor-queries.component';
import { TaskPropertiesComponent } from '../task-properties/task-properties.component';
import { BatchQueriesAgComponent } from '../batch-queries-ag/batch-queries-ag.component';
import { TaskManagementComponent } from '../task-management/task-management.component';
import { ReferralAddComponent } from '../referral-add/referral-add.component';
import { TaskReferralPropertiesComponent } from '../task-referral-properties/task-referral-properties.component';
import { TaskDirtyManagementComponent } from '../task-dirty-management/task-dirty-management.component';
import { TaskCompletionComponent } from '../task-completion/task-completion.component';
import { formatString } from '../../../utils/string-utils';

/**
 * Component for annotating and redacting PDFs.
 */
@Component({
	selector: 'annotation-editor',
	templateUrl: './annotation-editor.component.html',
	styleUrl: './annotation-editor.component.css'
})
export class AnnotationEditorComponent implements OnInit, OnDestroy {
	@Input() editorData: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isTaskMode: boolean = false;
	uuid!: string;
	viewerURL: SafeResourceUrl | null = null;
	dirtyData: any;
	customize: any;
	filteredAnnotations: any;

	editor: {
		hits: { [key: string]: any[] };
		hitsfilter: { groups: string[]; queries: string[] };
		numHits: number;
		customize: any;
	} = {
			hits: {},
			numHits: 0,
			hitsfilter: { groups: [], queries: [] },
			customize: {}
		};

	states = {
		hitsPanelOpen: true
	};

	// Keys used for sessionStorage data transfer
	// between this component and the iframe JS annotation editor
	iframeComm = {
		notificationId: 'dxnotify',
		hitsDataId: 'dxhits',
		pageDataId: 'dxpage',
		// queryDataId: 'dxquery',
		runSearchQuery: 'dxrunquery',
		dirtyId: 'dxdirty',
		filterAnnotationsId: 'dxfilterannots',
		getAnnotationsId: 'dxgetannots',
		getAnnotationsResultId: 'dxgetannotsresult'
	};

	constructor(
		private configService: ConfigService,
		private alertsService: AlertsService,
		private userService: UserService,
		private taskService: TaskService,
		private workflowUtilitiesService: WorkflowUtilitiesService,
		private sanitizer: DomSanitizer,
		private ngbModal: NgbModal,
		private ngbModalService: NgbActiveModal
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		this.isTaskMode = this.editorData.task ? true : false;
		this.editorData.title = this.editorData.task ? (this.editorData.task.taskName + " - " + this.editorData.task.batchName) : this.editorData.productTitle;
		this.editorData.subtitle = this.editorData.task ? this.editorData.task.productTitle : "";
		if (!this.editorData.task) this.editorData.task = {};
		this.uuid = this.editorData.task.productUUID || this.editorData.productID;
		// BE CAREFUL: a call to document provider with any search string besides "*" will lead to a IO resource lock
		this.editorData.searchString = encodeURIComponent(this.editorData.searchString) || "*";

		this.loadEditor();
	}

	ngOnDestroy(): void {
		this.stopTask(this.editorData.task.taskId);
		this.unloadEditor();
	}

	private loadEditor(): void {
		// listeners for data set by the annotation editor on sessionStorage:
		sessionStorage.removeItem(this.iframeComm.notificationId);
		window.addEventListener('storage', (event) => this.doNotify(event));

		sessionStorage.removeItem(this.iframeComm.hitsDataId);
		window.addEventListener('storage', (event) => this.processHitsData(event));

		//    	sessionStorage.removeItem(this.iframeComm.queryDataId);
		//    	window.addEventListener('storage', this.processQueryData);

		sessionStorage.removeItem(this.iframeComm.dirtyId);
		window.addEventListener('storage', (event) => this.processDirtyState(event));

		sessionStorage.removeItem(this.iframeComm.getAnnotationsResultId);
		window.addEventListener('storage', (event) => this.processAnnotationFilterResults(event));

		this.initCustomizations();

		this.loadViewerBooter();
		this.startTask(this.editorData.task.taskId);
	}

	private unloadEditor(): void {
		//    	console.log("START unloadEditor, sessionStorage keys:");
		//    	console.log(Object.keys(sessionStorage));

		window.removeEventListener('storage', this.doNotify);
		window.removeEventListener('storage', this.processHitsData);
		//		window.removeEventListener('storage', this.processQueryData);
		window.removeEventListener('storage', this.processDirtyState);
		window.removeEventListener('storage', this.processAnnotationFilterResults);

		sessionStorage.removeItem('dxeditorOpts');
		Object.keys(this.iframeComm).forEach((key) => {
			sessionStorage.removeItem(this.iframeComm[key as keyof typeof this.iframeComm]);
		});

		//		console.log("END unloadEditor, sessionStorage keys:");
		//		console.log(Object.keys(sessionStorage));
	}

	toggleHitsPanel(): void {
		this.states.hitsPanelOpen = !this.states.hitsPanelOpen;
	}

	showQueriesSelector(): void {
		const modalRef = this.ngbModal.open(EditorQueriesComponent);

		modalRef.result.then(
			(payload) => {
				let queryData = payload.result.caller.queryData;
				console.log(queryData);
				this.runSearch(queryData);
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	private runSearch(queryData: any): void {
		let searchRequest: {
			queryIDs: string[];
			queryListIDs: string[];
			nerIDs: string[];
			productIDs: string[];
			task: string;
			resultAction?: any;
			searchParagraphs?: any;
			timestamp: number;
		} = {
			queryIDs: [],
			queryListIDs: [],
			nerIDs: [],
			productIDs: [this.uuid],
			task: this.editorData.taskId,
			resultAction: queryData.resultAction,
			searchParagraphs: queryData.searchParagraphs,
			timestamp: new Date().getTime()
		};

		let qids = queryData.query_ids ? queryData.query_ids.split(',') : [];
		for (let i = 0; i < qids.length; i++) {
			searchRequest.queryIDs.push(qids[i]);
		}

		let qlists = queryData.query_list_ids ? queryData.query_list_ids.split(',') : [];
		for (let i = 0; i < qlists.length; i++) {
			searchRequest.queryListIDs.push(qlists[i]);
		}

		let ners = queryData.ner_ids ? queryData.ner_ids.split(',') : [];
		for (let i = 0; i < ners.length; i++) {
			searchRequest.nerIDs.push(ners[i]);
		}

		sessionStorage.setItem(this.iframeComm.runSearchQuery, JSON.stringify(searchRequest));
	}

	showTaskProperties(): void {
		const modalRef = this.ngbModal.open(TaskPropertiesComponent, {
			size: 'lg'
		});

		modalRef.componentInstance.task = this.editorData.task;

		modalRef.result.then(
			(result) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	showQueriesProperties(): void {
		// NOTE: no NER queries will be shown unless a full batch is passed in the resolved data
		// since NER is extracted from the batch.properties.
		// Alternative is to fetch the full batch in the workflow-batch-queries component
		const modalRef = this.ngbModal.open(BatchQueriesAgComponent);

		modalRef.componentInstance.batch = { batchId: this.editorData.task.batchId };

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	onReject(): void {
		const modalRef = this.ngbModal.open(TaskManagementComponent);

		let taskids: number[] = [this.editorData.task.taskId];

		modalRef.componentInstance.taskGroup = {
			taskids: taskids,
			mode: 'reject',
			comment: null,
			owner: this.userService.user.username
		};

		modalRef.result.then(
			(payload) => {
				// Close the main editor window
				this.close();
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	onRefer(): void {
		const modalRef = this.ngbModal.open(ReferralAddComponent);

		modalRef.componentInstance.taskid = this.editorData.task.taskId;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	onShowReferrals(): void {
		const modalRef = this.ngbModal.open(TaskReferralPropertiesComponent);

		modalRef.componentInstance.taskData = {
			taskId: this.editorData.task.taskId,
			subtitle: this.editorData.title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	processDirtyState(event: StorageEvent) {
		if (event.key === this.iframeComm?.dirtyId && event.newValue) {
			console.log("Dirty state updated:", event.newValue);
			this.dirtyData = JSON.parse(event.newValue);
			sessionStorage.removeItem(this.iframeComm.dirtyId);
		}
	}

	//	this.$on("dxTaskEvent", function (event, payload) {
	//		if ("clearDirtyEditorFlag" === payload?.result?.eventType) {
	//			this.dirtyData = null;
	//		} 
	//	});
	//
	//	this.$on("clearDirtyEditorFlag", function () {
	//		this.dirtyData = null;
	//	});

	private saveDirtySession(noprompt: boolean): void {
		if (this.dirtyData && this.dirtyData.isDirty) {
			const modalRef = this.ngbModal.open(TaskDirtyManagementComponent);

			modalRef.componentInstance.task = {
				noprompt: noprompt,
				taskid: this.editorData.task.taskId,
				uuid: this.uuid,
				annotations: this.dirtyData.annotations
			};

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	stopTask(id?: number): void {
		if (!id) return;
		this.taskService.stopTask(id).then(
			(response: any) => {
				if (this.dirtyData && this.dirtyData.isDirty) {
					this.saveDirtySession(false);
				}
				this.close();
			},
			(err: any) => {
				console.error("Failed to stop workflow task", err);
			}
		);
	}

	startTask(id?: number): void {
		if (!id) return;
		this.taskService.startTask(id).then(
			(response: any) => {
				// ok, no further action required
			},
			(err: any) => {
				console.error("Failed to start workflow task", err);
				this.alertsService.error({
					message: "Failed to start the task.",
					delay: 5000
				});
			}
		);
	}

	/**
	 * Callback for processing the JS annotation editor query hits added
	 * to sessionStorage.
	 */
	processHitsData(event: StorageEvent): void {
		if (event.key === this.iframeComm?.hitsDataId && event.newValue) {
			let jsonhits = JSON.parse(event.newValue);
			this.editor.numHits = jsonhits['TOTAL'];
			delete jsonhits['TOTAL'];
			sessionStorage.removeItem(this.iframeComm.hitsDataId);

			// Hits arrive grouped by category and sorted by page
			// eg. {"Exclude": [{annot}, {annot}], "Exempt": [{annot}]}

			this.editor.hitsfilter.queries = [];

			Object.entries(jsonhits).forEach(([groupname, annotArr]) => {
				(annotArr as any[]).forEach((annot: any) => {
					// Pluck out all of the query names into an array
					this.editor.hitsfilter.queries.push(annot.label);

					// Display the query color near the term in the hit list
					try {
						let match = (annot.color[0]).match(/r=(\d+),g=(\d+),b=(\d+)/);
						annot.rgbcolor = `rgb(${match[1]}, ${match[2]}, ${match[3]})`;
					}
					catch (err) {
						annot.rgbcolor = "rgb(0, 0, 0)";
					}
				});
			});
			this.editor.hits = jsonhits;

			// pluck out all of the groups into an array
			this.editor.hitsfilter.groups = Object.keys(jsonhits);
		}
	}

	showHitsByGroup(group: string): void {
		let idx = this.editor.hitsfilter.groups.indexOf(group);
		if (idx > -1) {
			this.editor.hitsfilter.groups.splice(idx, 1);
			this.editor.hitsfilter.queries = [];
		}
		else {
			this.editor.hitsfilter.groups.push(group);

			// Reset all query filters in this group
			let grouphits = this.editor.hits[group];
			grouphits.forEach((annot: any) => {
				let name = annot.queryName || annot.label;
				let idx = this.editor.hitsfilter.queries.indexOf(name);
				if (idx == -1) this.editor.hitsfilter.queries.push(name);
			});
		}

		// Show/hide all annotations matching the group
		let filter = { filterby: "group", filter: this.editor.hitsfilter.groups, timestamp: (new Date()).getTime() };
		sessionStorage.setItem(this.iframeComm.filterAnnotationsId, JSON.stringify(filter));
	}

	showHitsByQuery(query: string): void {
		let idx = this.editor.hitsfilter.queries.indexOf(query);
		if (idx > -1) {
			this.editor.hitsfilter.queries.splice(idx, 1);
		}
		else {
			this.editor.hitsfilter.queries.push(query);
		}

		// Show/hide all annotations matching the query
		let filter = { filterby: "query", filter: this.editor.hitsfilter.queries, timestamp: (new Date()).getTime() };
		sessionStorage.setItem(this.iframeComm.filterAnnotationsId, JSON.stringify(filter));
	}

	/*
	 * Jump to the page and highlight the annotations matching the parameters
	 */
	goToPage(page: number, queryId: string, label: string): void {
		let match = { page: page, queryId: queryId, label: label };
		sessionStorage.setItem(this.iframeComm.pageDataId, JSON.stringify(match));
	}

	doNotify(event: StorageEvent): void {
		if (event.key === this.iframeComm?.notificationId && event.newValue) {
			this.notify(event.newValue);
			sessionStorage.removeItem(this.iframeComm.notificationId);
		}
	}

	private notify(datastr: string): void {
		let data = JSON.parse(datastr);
		let notice = data.notice || {};
		if ("ok" == notice.status) {
			this.alertsService.success({
				message: notice.message,
				delay: 5000
			});
		}
		else if ("info" == notice.status || "warn" == notice.status) {
			this.alertsService.info({
				message: notice.message,
				delay: 5000
			});
		}
		else if ("error" == notice.status) {
			this.alertsService.error({
				message: notice.message,
				delay: 5000
			});
		}
	}

	// Deprecated
	/**
	 * Returns an array of all the annotations that match the key-value pairs listed in the filter.
	 * filter example: {state: "Redaction", ,status: "Rejected"}
	 */
	//	getFilteredAnnotations(filter: string): void {
	//		filter.timestamp = (new Date()).getTime();
	//		//		console.log("returning annotations by filter:");
	//		//		console.log(filter);
	//		sessionStorage.setItem(this.iframeComm.getAnnotationsId, JSON.stringify(filter));
	//	}

	processAnnotationFilterResults(event: StorageEvent): void {
		if (event.key === this.iframeComm?.getAnnotationsResultId && event.newValue) {
			//			console.log("IFRAME annot query result data: %s", event.newValue);

			this.filteredAnnotations = JSON.parse(event.newValue);
			sessionStorage.removeItem(this.iframeComm.getAnnotationsResultId);
		}
	}

	onComplete(): void {
		// ensure annotation session is saved
		if (this.dirtyData && this.dirtyData.isDirty) {
			this.saveDirtySession(true);
			return;
		}

		// Deprecated
		//		// get any annotations necessary to render a completion decision
		//		let taskDataForm = this.customize.taskDataForm ? this.customize.taskDataForm.name : null;
		//		if ('sf715TaskData' == taskDataForm || 'afdoTaskData' == taskDataForm) {
		//			// filter the annotations processed by the completion form
		//			let filter = this.customize.taskDataForm ? this.customize.taskDataForm.filter : null;
		//			if (filter) this.getFilteredAnnotations(filter);
		//		} else {
		this.openCompleteDialog();
		//		}
	}

	openCompleteDialog(): void {
		const modalRef = this.ngbModal.open(TaskCompletionComponent);

		modalRef.componentInstance.taskGroup = {
			taskids: [this.editorData.task.taskId],
			comment: null,
			owner: this.userService.user.username,
			annotations: this.filteredAnnotations,
			canSave: true
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
				this.close();
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	initCustomizations(): void {
		this.editor.customize = this.workflowUtilitiesService.getDomainCustomization("editor");
		this.editor.customize.main = this.workflowUtilitiesService.getDomainCustomization("main");

		let approveBtn = this.editor.customize.approveButton.find((button: any) => {
			return button.isDefault
		});

		if (this.editorData.task) {
			this.editor.customize.approve = this.editor.customize.approveButton.find((button: any) => {
				return button.taskName == this.editorData.task.taskName;
			});
		}

		if (!this.editor.customize.approve) {
			this.editor.customize.approve = approveBtn;
		}

		if (this.editor.customize.subtitle.fromMetadata && this.editorData.task.productMetadata) {
			let subtitle = [];
			for (let i = 0; i < this.editor.customize.subtitle.fromMetadata.length; i++) {
				let field = this.editor.customize.subtitle.fromMetadata[i];
				subtitle.push((this.editorData.task.productMetadata[field] || [''])[0]);
			}
			if (subtitle.length > 0) {
				this.editorData.subtitle = subtitle.join(' - ');
			}
		}
	}

	private loadViewerBooter(): void {
		// Store the annotation editor data in sessionStorage, where it will be located and parsed in annotation_editor_viewer.html
		let editorOpts = {
			// Used for communication to and from the editor iframe
			messaging: {
				notificationId: this.iframeComm.notificationId,
				hitsDataId: this.iframeComm.hitsDataId,
				// queryDataId: this.iframeComm.queryDataId,
				runSearchQuery: this.iframeComm.runSearchQuery,
				pageDataId: this.iframeComm.pageDataId,
				dirtyId: this.iframeComm.dirtyId,
				filterAnnotationsId: this.iframeComm.filterAnnotationsId,
				getAnnotationsId: this.iframeComm.getAnnotationsId,
				getAnnotationsResultId: this.iframeComm.getAnnotationsResultId
			},
			uuid: this.uuid,
			searchString: this.editorData.searchString,
			file: formatString(this.configService.getConfig().document.documentProviderHighlightURL, this.uuid, this.editorData.searchString),
			user: {
				username: this.userService.user.username,
				group: this.userService.user.group,
				domain: this.userService.user.domain,
				email: this.userService.user.email,
				firstname: this.userService.user.firstName,
				lastname: this.userService.user.lastName,
				libraries: []
			},
			permissions: this.userPermissions,
			config: this.configService.getConfig().dx,
			controlsTemplate: this.workflowUtilitiesService.getDomainCustomization("annotationEditor")?.annotationEditor?.template,
			taskId: this.editorData.task.taskId,
			taskName: this.editorData.task.taskName,
			sessionId: this.editorData.task.annotationSessionId
		};
		editorOpts.config.document = this.configService.getConfig().document;
		editorOpts.config.upload = this.configService.getConfig().upload;

		if (this.userService.user.properties) {
			editorOpts.user.libraries = this.userService.user.properties.awlibraryObjectList.filter((v: any) => v.accessLevel > 2);
		}

		sessionStorage.setItem('dxeditorOpts', JSON.stringify(editorOpts));

		// pdfjs viewer loads the file using the 'file' parameter
		this.generateViewerURL(editorOpts.file);
	}

	private generateViewerURL(editorOptsFile: string): void {
		const unsafeUrl = "app/js/annotation-editor/annotation_editor_viewer.html?file=" + encodeURIComponent(editorOptsFile);
		// Tell Angular that this URL is safe to use
		this.viewerURL = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
	}

	/*
	 * Deprecated (AFDO Air Force pilot)
	 */
	//	// TODO: need a better way of showing the editor instead of by default on annotation click
	//	// Opens a query editor for the query that produced the annotation
	//	this.processQueryData = function (event) {
	//		if (event.key != this.iframeComm.queryDataId) { return; }
	//		$timeout(function () {
	//			console.log("IFRAME query data: %s", event.newValue);
	//
	//			let annotationQuery = JSON.parse(event.newValue);
	//			sessionStorage.removeItem(this.iframeComm.queryDataId);
	//
	//			if (this.workflowUtilitiesService.isEmptyObj(annotationQuery) || !annotationQuery.queryId) {
	//				return;
	//			}
	//
	//			let modalInstance = $uibModal.open({
	//				animation: this.animationsEnabled,
	//				templateUrl: '~/../app/js/workflow/query-editor.html',
	//				windowClass: 'dx-modal',
	//				controller: 'DXQueryEditorCtrl',
	//				resolve: {
	//					args: function () {
	//						return {
	//							querydata: annotationQuery,
	//							taskId: this.editorData.task.taskId,
	//							productID: this.uuid,
	//							iframeComm: { runSearchQuery: this.iframeComm.runSearchQuery },
	//							owner: this.user.username
	//						}
	//					}
	//				}
	//			});
	//		}, 500);
	//	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbModalService.dismiss();
	}
}
